import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import {
  FiPlus, FiTrash2, FiSearch, FiUser, FiChevronsUp, FiChevronsDown,
  FiChevronLeft, FiChevronRight, FiFileText, FiEdit, FiEye,
  FiPrinter, FiRefreshCw,
} from "react-icons/fi";
import { baseurl } from "../../baseurl";
import axios from "axios";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

/* ─── CSS ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --aq-teal:      #0a5556;
  --aq-teal-lite: #14b8a6;
  --aq-gold:      #FFD166;
  --aq-bg:        #eef6f6;
  --aq-white:     #ffffff;
  --aq-border:    rgba(10,85,86,0.10);
  --aq-text:      #0f2a2a;
  --aq-muted:     #5a8080;
  --aq-danger:    #ef4444;
  --aq-success:   #10b981;
  --aq-warning:   #f59e0b;
  --aq-info:      #3b82f6;
}

@keyframes aq__fadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
@keyframes aq__shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes aq__pulse   { 0%,100%{opacity:1} 50%{opacity:.5} }

.aq__wrap {
  font-family:'DM Sans',sans-serif;
  padding:22px 22px 40px;
  background:var(--aq-bg);
  min-height:100vh;
  animation:aq__fadeUp 0.4s ease both;
}

/* ── header ── */
.aq__hd {
  display:flex; align-items:flex-start; justify-content:space-between;
  gap:16px; margin-bottom:20px; flex-wrap:wrap;
}
.aq__hd-left { display:flex; align-items:center; gap:14px; }
.aq__hd-icon {
  width:48px; height:48px; border-radius:14px; flex-shrink:0;
  background:linear-gradient(135deg,var(--aq-teal),var(--aq-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:20px; color:#fff;
  box-shadow:0 6px 18px rgba(10,85,86,0.26);
}
.aq__hd-title { font-family:'Sora',sans-serif; font-size:20px; font-weight:800; color:var(--aq-text); letter-spacing:-0.03em; margin:0 0 3px; }
.aq__hd-sub   { font-size:12.5px; color:var(--aq-muted); margin:0; }

.aq__create-btn {
  display:inline-flex; align-items:center; gap:8px;
  padding:10px 20px; border-radius:12px;
  font-size:13.5px; font-family:'Sora',sans-serif; font-weight:700;
  color:#fff; letter-spacing:0.03em;
  background:linear-gradient(135deg,var(--aq-teal),var(--aq-teal-lite));
  border:none; text-decoration:none;
  box-shadow:0 4px 16px rgba(10,85,86,0.28);
  transition:transform 0.14s, box-shadow 0.18s;
}
.aq__create-btn:hover { transform:translateY(-1px); box-shadow:0 8px 24px rgba(10,85,86,0.34); color:#fff; text-decoration:none; }

/* ── stat chips ── */
.aq__stats { display:flex; gap:10px; margin-bottom:20px; flex-wrap:wrap; }
.aq__stat {
  display:flex; align-items:center; gap:10px;
  background:var(--aq-white); border:1px solid var(--aq-border);
  border-radius:13px; padding:11px 16px;
  box-shadow:0 2px 10px rgba(10,85,86,0.05);
  flex:1; min-width:130px;
}
.aq__stat-dot  { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.aq__stat-label{ font-size:11.5px; color:var(--aq-muted); font-weight:500; }
.aq__stat-val  { font-family:'Sora',sans-serif; font-size:15px; font-weight:800; color:var(--aq-text); }

/* ── notice ── */
.aq__notice {
  display:inline-flex; align-items:center; gap:7px;
  background:rgba(239,68,68,0.07); border:1px solid rgba(239,68,68,0.18);
  border-radius:10px; padding:7px 14px; margin-bottom:16px;
  font-size:12px; font-weight:600; color:#dc2626;
}

/* ── main card ── */
.aq__card {
  background:var(--aq-white); border-radius:20px;
  border:1px solid var(--aq-border);
  box-shadow:0 2px 20px rgba(10,85,86,0.07),0 8px 40px rgba(0,0,0,0.03);
  overflow:hidden; position:relative;
  animation:aq__fadeUp 0.5s 0.06s ease both;
}
.aq__card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--aq-teal) 0%,var(--aq-teal-lite) 55%,var(--aq-gold) 100%);
}

/* ── toolbar ── */
.aq__toolbar {
  display:flex; align-items:center; justify-content:space-between;
  gap:14px; padding:18px 22px; flex-wrap:wrap;
  border-bottom:1px solid var(--aq-border);
}
.aq__toolbar-left  { display:flex; align-items:center; gap:10px; }
.aq__show-lbl { font-size:12.5px; color:var(--aq-muted); font-weight:500; white-space:nowrap; }
.aq__select {
  padding:7px 11px; font-size:13px; font-family:'DM Sans',sans-serif;
  color:var(--aq-text); background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.15); border-radius:9px;
  outline:none; cursor:pointer;
  transition:border-color 0.18s, box-shadow 0.18s;
}
.aq__select:focus { border-color:var(--aq-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); }

/* status filter pills */
.aq__filters { display:flex; gap:6px; flex-wrap:wrap; }
.aq__filter-pill {
  padding:5px 14px; border-radius:50px; border:1.5px solid var(--aq-border);
  font-size:11.5px; font-weight:700; font-family:'Sora',sans-serif;
  color:var(--aq-muted); background:var(--aq-bg); cursor:pointer;
  transition:all 0.16s; white-space:nowrap;
}
.aq__filter-pill:hover { border-color:var(--aq-teal); color:var(--aq-teal); }
.aq__filter-pill--active { background:var(--aq-teal) !important; border-color:var(--aq-teal) !important; color:#fff !important; }

.aq__search {
  display:flex; align-items:center; gap:8px;
  background:#f3fafa; border:1.5px solid rgba(10,85,86,0.15);
  border-radius:10px; padding:7px 13px;
  transition:border-color 0.18s, box-shadow 0.18s;
}
.aq__search:focus-within { border-color:var(--aq-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.aq__search-icon  { color:var(--aq-muted); font-size:14px; flex-shrink:0; }
.aq__search-input {
  border:none; outline:none; background:transparent;
  font-size:13px; font-family:'DM Sans',sans-serif; color:var(--aq-text);
  width:200px;
}
.aq__search-input::placeholder { color:#9ababa; }

/* ── table ── */
.aq__table-wrap { overflow-x:auto; }
.aq__table { width:100%; border-collapse:collapse; }
.aq__table thead th {
  font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--aq-teal);
  background:rgba(10,85,86,0.04); padding:11px 18px;
  white-space:nowrap; border-bottom:1px solid var(--aq-border);
  cursor:pointer; user-select:none;
}
.aq__table thead th:hover { color:var(--aq-teal-lite); }
.aq__sort-icon { display:inline-flex; vertical-align:middle; margin-left:4px; opacity:.4; font-size:13px; }
.aq__sort-icon--active { opacity:1; color:var(--aq-teal-lite); }

.aq__table tbody tr { border-bottom:1px solid rgba(10,85,86,0.055); transition:background 0.15s; }
.aq__table tbody tr:last-child { border-bottom:none; }
.aq__table tbody tr:hover { background:rgba(10,85,86,0.025); }
.aq__table tbody td { padding:12px 18px; font-size:13.5px; color:var(--aq-text); vertical-align:middle; }

/* cells */
.aq__qid {
  display:inline-flex; align-items:center; gap:6px;
  font-family:'Sora',sans-serif; font-size:12px; font-weight:700;
  background:rgba(10,85,86,0.07); color:var(--aq-teal);
  border:1px solid rgba(10,85,86,0.14); border-radius:7px;
  padding:3px 10px; white-space:nowrap;
}
.aq__customer { display:flex; align-items:center; gap:10px; }
.aq__cust-av {
  width:30px; height:30px; border-radius:9px; flex-shrink:0;
  background:linear-gradient(135deg,var(--aq-teal),var(--aq-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:11px; font-weight:700; color:#fff; font-family:'Sora',sans-serif;
}
.aq__cust-name { font-size:13px; font-weight:600; color:var(--aq-text); }
.aq__cust-sub  { font-size:11px; color:var(--aq-muted); margin-top:1px; }
.aq__date { font-size:12.5px; color:var(--aq-muted); }
.aq__amount {
  font-family:'Sora',sans-serif; font-size:13.5px; font-weight:800;
  color:var(--aq-teal);
}

/* status badge */
.aq__badge {
  display:inline-flex; align-items:center; gap:5px;
  font-size:11px; font-weight:700; font-family:'Sora',sans-serif;
  border-radius:50px; padding:3px 10px; white-space:nowrap;
}
.aq__badge-dot { width:6px; height:6px; border-radius:50%; }
.aq__badge--pending   { background:rgba(245,158,11,0.10); color:#b45309; border:1px solid rgba(245,158,11,0.26); }
.aq__badge--pending .aq__badge-dot    { background:#f59e0b; }
.aq__badge--accepted  { background:rgba(59,130,246,0.09); color:#2563eb; border:1px solid rgba(59,130,246,0.22); }
.aq__badge--accepted .aq__badge-dot   { background:#3b82f6; }
.aq__badge--rejected  { background:rgba(239,68,68,0.08);  color:#dc2626; border:1px solid rgba(239,68,68,0.20); }
.aq__badge--rejected .aq__badge-dot   { background:#ef4444; }
.aq__badge--invoiced  { background:rgba(16,185,129,0.09); color:#047857; border:1px solid rgba(16,185,129,0.22); }
.aq__badge--invoiced .aq__badge-dot   { background:#10b981; }

/* convert btn */
.aq__convert-btn {
  display:inline-flex; align-items:center; gap:6px;
  padding:5px 12px; border-radius:9px;
  font-size:11.5px; font-family:'Sora',sans-serif; font-weight:700;
  color:#047857; background:rgba(16,185,129,0.09);
  border:1.5px solid rgba(16,185,129,0.24); cursor:pointer;
  transition:all 0.16s;
}
.aq__convert-btn:hover { background:rgba(16,185,129,0.15); border-color:#10b981; }
.aq__invoiced-lbl { font-size:11px; font-weight:700; color:#047857; }

/* action icons */
.aq__actions { display:flex; align-items:center; gap:4px; justify-content:center; }
.aq__action-btn {
  width:30px; height:30px; border-radius:8px; border:none;
  background:transparent; display:flex; align-items:center; justify-content:center;
  font-size:14px; cursor:pointer; color:var(--aq-muted);
  transition:background 0.15s, color 0.15s;
}
.aq__action-btn:hover            { background:rgba(10,85,86,0.08); color:var(--aq-teal); }
.aq__action-btn--danger:hover    { background:rgba(239,68,68,0.09); color:var(--aq-danger); }
.aq__action-btn--disabled        { opacity:0.35; cursor:not-allowed !important; pointer-events:none; }

/* empty */
.aq__empty { padding:60px 20px; text-align:center; }
.aq__empty-icon { width:58px; height:58px; border-radius:18px; margin:0 auto 14px; background:rgba(10,85,86,0.07); display:flex; align-items:center; justify-content:center; font-size:26px; }
.aq__empty-title { font-family:'Sora',sans-serif; font-size:16px; font-weight:700; color:var(--aq-text); margin-bottom:5px; }
.aq__empty-sub   { font-size:13px; color:var(--aq-muted); }

/* shimmer skeletons */
.aq__skel {
  height:14px; border-radius:6px;
  background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%);
  background-size:600px 100%;
  animation:aq__shimmer 1.4s linear infinite;
}
.aq__skel-circle { width:30px; height:30px; border-radius:9px; flex-shrink:0; }

/* footer */
.aq__footer {
  display:flex; align-items:center; justify-content:space-between;
  padding:14px 22px; border-top:1px solid var(--aq-border);
  flex-wrap:wrap; gap:10px;
}
.aq__footer-info { font-size:12.5px; color:var(--aq-muted); }
.aq__footer-info strong { color:var(--aq-text); }

/* pagination */
.aq__pagination { display:flex; align-items:center; gap:4px; }
.aq__page-btn {
  min-width:32px; height:32px; border-radius:9px; border:1.5px solid var(--aq-border);
  background:var(--aq-white); font-size:12px; font-weight:600;
  color:var(--aq-muted); cursor:pointer; padding:0 8px;
  display:flex; align-items:center; justify-content:center; gap:3px;
  transition:all 0.15s;
}
.aq__page-btn:hover:not(:disabled) { border-color:var(--aq-teal); color:var(--aq-teal); background:rgba(10,85,86,0.05); }
.aq__page-btn--active { background:var(--aq-teal) !important; border-color:var(--aq-teal) !important; color:#fff !important; font-family:'Sora',sans-serif; }
.aq__page-btn:disabled { opacity:.36; cursor:not-allowed; }

@media(max-width:700px){ .aq__filters{ display:none; } .aq__search-input{ width:140px; } }
`;

/* ─── Helpers ─── */
const formatCurrency = (amount) =>
  Number(amount).toLocaleString("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0] || "").join("").toUpperCase() || "?";

const STATUS_LIST = ["All", "Pending", "Accepted", "Rejected", "Invoiced"];

function StatusBadge({ status }) {
  const map = {
    Pending:  "aq__badge--pending",
    Accepted: "aq__badge--accepted",
    Rejected: "aq__badge--rejected",
    Invoiced: "aq__badge--invoiced",
    Converted:"aq__badge--invoiced",
  };
  const cls = map[status] || "aq__badge--pending";
  return <span className={`aq__badge ${cls}`}><span className="aq__badge-dot" />{status}</span>;
}

function SortIcon({ columnKey, sortBy }) {
  const isActive = sortBy.key === columnKey;
  const cls = `aq__sort-icon${isActive ? " aq__sort-icon--active" : ""}`;
  return isActive && sortBy.direction === "asc"
    ? <FiChevronsUp className={cls} />
    : <FiChevronsDown className={cls} />;
}

function CustomPagination({ totalItems, itemsPerPage, currentPage, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems === 0) return null;

  let start = Math.max(1, currentPage - 2);
  let end   = Math.min(totalPages, currentPage + 2);
  if (currentPage <= 3)            { start = 1; end = Math.min(5, totalPages); }
  else if (currentPage + 2 >= totalPages) { end = totalPages; start = Math.max(1, totalPages - 4); }
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="aq__pagination">
      <button className="aq__page-btn" onClick={() => onPageChange(1)} disabled={currentPage === 1}>First</button>
      <button className="aq__page-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
        <FiChevronLeft size={12} /> Prev
      </button>
      {pages.map((p) => (
        <button key={p} className={`aq__page-btn ${currentPage === p ? "aq__page-btn--active" : ""}`} onClick={() => onPageChange(p)}>
          {p}
        </button>
      ))}
      <button className="aq__page-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
        Next <FiChevronRight size={12} />
      </button>
      <button className="aq__page-btn" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages}>Last</button>
    </div>
  );
}

/* ─── Main ─── */
function AllQuotation() {
  const [quotations, setQuotations]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchTerm, setSearchTerm]     = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateRange, setDateRange]       = useState({ start: "", end: "" });
  const [sortBy, setSortBy]             = useState({ key: "quotationDate", direction: "desc" });
  const [currentPage, setCurrentPage]   = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const token = localStorage.getItem("auth_token");

  /* ── fetch (unchanged) ── */
  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllQuotationsByBusiness`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      console.log("res", res);
      if (res.data.success) {
        const mappedQuotations = res.data.data.map((q) => ({
          id:               q.id,
          quotation_number: q.quotation_number,
          customerName:     q.quotation_data.customer?.name || "-",
          quotationDate:    q.quotation_data.quotationDate,
          validityDate:     q.quotation_data.validityDate || q.quotation_data.dueDate,
          grandTotal:       Number(q.grand_total),
          status:           q.status || "Pending",
        }));
        setQuotations(mappedQuotations);
      }
    } catch (error) {
      console.error("❌ Fetch quotations error:", error);
      Swal.fire("Error", "Failed to load quotations", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotations(); }, [token]);

  /* ── process (unchanged) ── */
  const processedQuotations = useMemo(() => {
    let list = [...quotations];
    if (searchTerm.trim()) {
      const s = searchTerm.trim().toLowerCase();
      list = list.filter(i =>
        i.quotation_number.toLowerCase().includes(s) ||
        i.customerName.toLowerCase().includes(s) ||
        String(i.grandTotal).toLowerCase().includes(s)
      );
    }
    if (statusFilter !== "All")  list = list.filter(i => i.status === statusFilter);
    if (dateRange.start)         list = list.filter(i => i.quotationDate >= dateRange.start);
    if (dateRange.end)           list = list.filter(i => i.quotationDate <= dateRange.end);
    list.sort((a, b) => {
      const aVal = a[sortBy.key], bVal = b[sortBy.key];
      const cmp = sortBy.key === "grandTotal" ? Number(aVal) - Number(bVal) : String(aVal).localeCompare(String(bVal));
      return sortBy.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [quotations, searchTerm, statusFilter, dateRange, sortBy]);

  useEffect(() => setCurrentPage(1), [searchTerm, statusFilter, dateRange, sortBy, itemsPerPage]);

  const totalItems = processedQuotations.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex   = Math.min(startIndex + itemsPerPage, totalItems);
  const paginated  = processedQuotations.slice(startIndex, endIndex);

  const handleSort = (key) => {
    setSortBy(prev => prev.key === key
      ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
      : { key, direction: "desc" }
    );
  };

  /* ── handlers (unchanged) ── */
  const handleEditQuotation    = (id) => { window.location.href = `/quotation-edit/${btoa(id.toString())}`; };
  const handlePrintQuotation   = (id) => { window.location.href = `/quotation-view/${btoa(id.toString())}`; };

  const handleConvertToInvoice = async (id) => {
    Swal.fire({
      title: 'Convert to Invoice?',
      text: "This will create a new GST Invoice based on this quotation.",
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Convert!',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await axios.post(
            `${baseurl}convertQuotationToInvoice`,
            { quotationId: id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log("res", res);
          return res.data;
        } catch (error) {
          Swal.showValidationMessage(`Request failed: ${error.response?.data?.message || error.message}`);
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed && result.value?.success) {
        Swal.fire('Success!', `Quotation converted. Invoice No: ${result.value.invoice_number}`, 'success');
        fetchQuotations();
      }
    });
  };

  const handleDeleteQuotation = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete quotation ${id}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setQuotations(prev => prev.filter(item => item.id !== id));
          Swal.fire('Deleted!', 'Quotation has been deleted.', 'success');
        } catch (error) {
          Swal.fire('Error', 'Failed to delete quotation', 'error');
        }
      }
    });
  };

  /* stat counts */
  const pendingCnt  = quotations.filter(q => q.status === "Pending").length;
  const acceptedCnt = quotations.filter(q => q.status === "Accepted").length;
  const invoicedCnt = quotations.filter(q => q.status === "Invoiced" || q.status === "Converted").length;
  const totalAmt    = quotations.reduce((s, q) => s + q.grandTotal, 0);

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="aq__wrap">

          {/* ── Header ── */}
          <div className="aq__hd">
            <div className="aq__hd-left">
              <div className="aq__hd-icon"><FiFileText /></div>
              <div>
                <div className="aq__hd-title">All Quotations</div>
                <div className="aq__hd-sub">Manage quotations, proposals &amp; client estimations</div>
              </div>
            </div>
            <Link to="/estimate-create" className="aq__create-btn">
              <FiPlus size={15} /> Create Quotation
            </Link>
          </div>

          {/* ── Stats ── */}
          <div className="aq__stats">
            <div className="aq__stat">
              <div className="aq__stat-dot" style={{ background:"#0a5556" }} />
              <div><div className="aq__stat-label">Total</div><div className="aq__stat-val">{quotations.length}</div></div>
            </div>
            <div className="aq__stat">
              <div className="aq__stat-dot" style={{ background:"#f59e0b" }} />
              <div><div className="aq__stat-label">Pending</div><div className="aq__stat-val">{pendingCnt}</div></div>
            </div>
            <div className="aq__stat">
              <div className="aq__stat-dot" style={{ background:"#3b82f6" }} />
              <div><div className="aq__stat-label">Accepted</div><div className="aq__stat-val">{acceptedCnt}</div></div>
            </div>
            <div className="aq__stat">
              <div className="aq__stat-dot" style={{ background:"#10b981" }} />
              <div><div className="aq__stat-label">Invoiced</div><div className="aq__stat-val">{invoicedCnt}</div></div>
            </div>
            <div className="aq__stat">
              <div className="aq__stat-dot" style={{ background:"#ef4444" }} />
              <div>
                <div className="aq__stat-label">Total Value</div>
                <div className="aq__stat-val" style={{ fontSize:13 }}>{formatCurrency(totalAmt)}</div>
              </div>
            </div>
          </div>

          {/* ── Notice ── */}
          <div className="aq__notice">
            ⚠ Once Invoiced, a quotation cannot be edited or deleted.
          </div>

          {/* ── Card ── */}
          <div className="aq__card">

            {/* Toolbar */}
            <div className="aq__toolbar">
              <div className="aq__toolbar-left" style={{ flexWrap:"wrap", gap:10 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span className="aq__show-lbl">Show</span>
                  <select className="aq__select" value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={quotations.length}>All</option>
                  </select>
                </div>
                <div className="aq__filters">
                  {STATUS_LIST.map((s) => (
                    <button key={s} className={`aq__filter-pill ${statusFilter === s ? "aq__filter-pill--active" : ""}`}
                      onClick={() => setStatusFilter(s)}>{s}</button>
                  ))}
                </div>
              </div>
              <div className="aq__search">
                <FiSearch className="aq__search-icon" />
                <input className="aq__search-input" placeholder="Search ID, customer, amount…"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
              </div>
            </div>

            {/* Table */}
            <div className="aq__table-wrap">
              <table className="aq__table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("quotation_number")}>Quotation ID <SortIcon columnKey="quotation_number" sortBy={sortBy} /></th>
                    <th onClick={() => handleSort("customerName")}>Customer <SortIcon columnKey="customerName" sortBy={sortBy} /></th>
                    <th onClick={() => handleSort("quotationDate")}>Date <SortIcon columnKey="quotationDate" sortBy={sortBy} /></th>
                    <th onClick={() => handleSort("validityDate")}>Valid Until <SortIcon columnKey="validityDate" sortBy={sortBy} /></th>
                    <th style={{ textAlign:"right" }} onClick={() => handleSort("grandTotal")}>Amount <SortIcon columnKey="grandTotal" sortBy={sortBy} /></th>
                    <th>Status</th>
                    <th style={{ textAlign:"center" }}>Convert</th>
                    <th style={{ textAlign:"center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        <td><div className="aq__skel" style={{ width:90 }} /></td>
                        <td>
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <div className="aq__skel aq__skel-circle" />
                            <div><div className="aq__skel" style={{ width:120, marginBottom:6 }} /><div className="aq__skel" style={{ width:80 }} /></div>
                          </div>
                        </td>
                        {[80,80,70,60,90,80].map((w, j) => (
                          <td key={j}><div className="aq__skel" style={{ width:w }} /></td>
                        ))}
                      </tr>
                    ))
                  ) : paginated.length === 0 ? (
                    <tr><td colSpan="8">
                      <div className="aq__empty">
                        <div className="aq__empty-icon">🔍</div>
                        <div className="aq__empty-title">No quotations found</div>
                        <div className="aq__empty-sub">Try adjusting your search or filters, or create a new quotation.</div>
                      </div>
                    </td></tr>
                  ) : paginated.map((q) => (
                    <tr key={q.id}>
                      <td><span className="aq__qid"><FiFileText size={11} />{q.quotation_number}</span></td>
                      <td>
                        <div className="aq__customer">
                          <div className="aq__cust-av">{getInitials(q.customerName)}</div>
                          <div>
                            <div className="aq__cust-name">{q.customerName}</div>
                            <div className="aq__cust-sub">ID: {q.quotation_number}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="aq__date">{q.quotationDate}</span></td>
                      <td><span className="aq__date">{q.validityDate}</span></td>
                      <td style={{ textAlign:"right" }}><span className="aq__amount">{formatCurrency(q.grandTotal)}</span></td>
                      <td><StatusBadge status={q.status} /></td>
                      <td style={{ textAlign:"center" }}>
                        {q.status !== "Invoiced" ? (
                          <button className="aq__convert-btn" onClick={() => handleConvertToInvoice(q.id)} title="Convert to GST Invoice">
                            <FiRefreshCw size={12} /> Invoice
                          </button>
                        ) : (
                          <span className="aq__invoiced-lbl">✓ Invoiced</span>
                        )}
                      </td>
                      <td>
                        <div className="aq__actions">
                          <button
                            className={`aq__action-btn ${q.status === "Invoiced" ? "aq__action-btn--disabled" : ""}`}
                            title={q.status === "Invoiced" ? "Cannot edit invoiced quotation" : "Edit"}
                            onClick={() => q.status !== "Invoiced" && handleEditQuotation(q.id)}
                          >
                            <FiEdit size={14} />
                          </button>
                          <button
                            className={`aq__action-btn aq__action-btn--danger ${q.status === "Invoiced" ? "aq__action-btn--disabled" : ""}`}
                            title={q.status === "Invoiced" ? "Cannot delete invoiced quotation" : "Delete"}
                            onClick={() => q.status !== "Invoiced" && handleDeleteQuotation(q.id)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="aq__footer">
              <div className="aq__footer-info">
                Showing <strong>{totalItems === 0 ? 0 : startIndex + 1}</strong> to{" "}
                <strong>{endIndex}</strong> of <strong>{totalItems}</strong> entries
              </div>
              <CustomPagination
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>

        </div>
      </main>
    </>
  );
}

export default AllQuotation;
